'use client';

import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  Drawer,
  Portal,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';

import { MdOutlineShoppingBag } from 'react-icons/md';
import { CartContext } from '@/contexts/CartContext';
import { useRouter } from 'next/navigation';
import CartItem from './Item';
import { hasProduct, pushDataLayerEvent, toEcommerceItem } from '@/utils/dataLayer';

const Cart = () => {
  const [open, setOpen] = useState(false);

  const { push } = useRouter();

  const { items, revalidateCart } = useContext(CartContext);

  useEffect(() => {
    if (open) revalidateCart();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || items.length === 0) return;

    pushDataLayerEvent('view_cart', {
      ecommerce: {
        currency: 'EUR',
        value: items.reduce((acc, item) => acc + item.total_price_tax, 0),
        items: items
          .filter(hasProduct)
          .map((item) => toEcommerceItem(item.product, item.quantity)),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalQty = items.reduce((acc, curr) => acc + curr.quantity, 0);
  const totalPrice = items.reduce((acc, item) => acc + item.total_price_tax, 0);

  const formatPrice = (val: number) =>
    new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
      currencySign: 'accounting',
    }).format(val);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnEscape={false}
      size="xs"
      placement="end"
    >
      <Drawer.Backdrop />
      <Drawer.Trigger asChild>
        <IconButton
          aria-label={
            totalQty > 0
              ? `Carrinho de compras, ${totalQty} artigo${totalQty !== 1 ? 's' : ''}`
              : 'Carrinho de compras'
          }
          bg="transparent"
          border="1px solid transparent"
          _hover={{ bg: 'rgba(0,176,202,0.07)', borderColor: 'rgba(0,176,202,0.2)' }}
          _active={{ bg: 'rgba(0,176,202,0.12)' }}
          _focus={{ bg: 'transparent' }}
          borderRadius="10px"
          transition="all 0.2s"
          w={{ base: '36px', md: '38px' }}
          h={{ base: '36px', md: '38px' }}
          pos="relative"
        >
          <Icon
            w={{ base: '20px', md: '22px' }}
            h={{ base: '20px', md: '22px' }}
            color={{ base: '#0A1B2B', _dark: '#C8DCE8' }}
          >
            <MdOutlineShoppingBag />
          </Icon>
          {totalQty > 0 && (
            <Flex
              bg="#00B0CA"
              color="#fff"
              minW="16px"
              h="16px"
              px="3px"
              align="center"
              justify="center"
              fontSize="9px"
              fontWeight="800"
              borderRadius="full"
              pos="absolute"
              top="-3px"
              right="-3px"
              boxShadow="0 2px 6px rgba(0,176,202,0.45)"
              lineHeight="1"
            >
              {totalQty}
            </Flex>
          )}
        </IconButton>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            bg={{ base: '#FAFBFC', _dark: '#08131E' }}
            zIndex="30"
            borderRadius="16px 0 0 16px"
            boxShadow="-6px 0 40px rgba(0,0,0,0.14)"
            right="0"
            top="0"
            bottom="0"
            h="100svh"
            borderLeft="1px solid"
            borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.06)' }}
            pos="relative"
            overflow="hidden"
            display="flex"
            flexDir="column"
          >
            {/* Gradient top strip */}
            <Box
              pos="absolute"
              top="0"
              left="0"
              right="0"
              h="3px"
              bgGradient="linear(to-r, #0A1B2B, #00B0CA 50%, #0A1B2B)"
              zIndex="1"
            />

            {/* Header */}
            <Flex
              pt="20px"
              pb="14px"
              px="20px"
              align="center"
              justify="space-between"
              borderBottom="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.05)' }}
              flexShrink={0}
            >
              <Flex align="center" gap="10px">
                <Flex
                  w="34px"
                  h="34px"
                  borderRadius="10px"
                  bg="rgba(0,176,202,0.1)"
                  border="1px solid rgba(0,176,202,0.2)"
                  align="center"
                  justify="center"
                >
                  <Icon w="18px" h="18px" color="#00B0CA">
                    <MdOutlineShoppingBag />
                  </Icon>
                </Flex>
                <Box>
                  <Text
                    fontSize="15px"
                    fontWeight="700"
                    color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                    lineHeight="1.2"
                  >
                    O Meu Carrinho
                  </Text>
                  <Text
                    fontSize="11px"
                    color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }}
                  >
                    {totalQty} {totalQty === 1 ? 'artigo' : 'artigos'}
                  </Text>
                </Box>
              </Flex>

              {/* Close button */}
              <Box
                w="28px"
                h="28px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.07)' }}
                cursor="pointer"
                transition="all 0.18s"
                _hover={{ bg: 'rgba(0,176,202,0.15)' }}
                onClick={() => setOpen(false)}
              >
                <Text fontSize="13px" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }} lineHeight="1">
                  ✕
                </Text>
              </Box>
            </Flex>

            {/* Items list */}
            <Flex
              flex="1"
              overflowY="auto"
              flexDir="column"
              px="14px"
              pt="12px"
              pb="6px"
              gap="0"
              css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,176,202,0.3)', borderRadius: '4px' },
              }}
            >
              {items.length === 0 ? (
                <Flex
                  flex="1"
                  flexDir="column"
                  align="center"
                  justify="center"
                  gap="12px"
                  py="48px"
                  opacity="0.45"
                >
                  <Icon w="40px" h="40px" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                    <MdOutlineShoppingBag />
                  </Icon>
                  <Text fontSize="13px" fontWeight="500" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }} textAlign="center">
                    O seu carrinho está vazio
                  </Text>
                </Flex>
              ) : (
                items.map((item) => <CartItem key={item.id} item={item} />)
              )}
            </Flex>

            {/* Footer: total + checkout */}
            {items.length > 0 && (
              <Flex
                flexDir="column"
                px="16px"
                pt="14px"
                pb="20px"
                borderTop="1px solid"
                borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.05)' }}
                bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
                flexShrink={0}
                gap="12px"
              >
                {/* Total row */}
                <Flex
                  justify="space-between"
                  align="center"
                  bg={{ base: 'rgba(0,176,202,0.04)', _dark: 'rgba(0,176,202,0.08)' }}
                  borderRadius="12px"
                  px="14px"
                  py="12px"
                >
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    letterSpacing="0.8px"
                    textTransform="uppercase"
                    color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
                  >
                    Total · {totalQty} {totalQty === 1 ? 'artigo' : 'artigos'}
                  </Text>
                  <Text
                    fontSize="18px"
                    fontWeight="800"
                    color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                    letterSpacing="-0.5px"
                  >
                    {formatPrice(totalPrice)}
                  </Text>
                </Flex>

                {/* Checkout CTA */}
                <Button
                  onClick={() => push('/Checkout')}
                  bg="#00B0CA"
                  color="#fff"
                  w="full"
                  h="44px"
                  borderRadius="full"
                  fontWeight="700"
                  fontSize="13px"
                  letterSpacing="0.04em"
                  boxShadow="0 4px 20px rgba(0,176,202,0.35)"
                  transition="all 0.22s"
                  _hover={{ bg: '#009AB8', transform: 'translateY(-1px)', boxShadow: '0 8px 28px rgba(0,176,202,0.45)' }}
                  _active={{ transform: 'translateY(0)', boxShadow: '0 2px 10px rgba(0,176,202,0.3)' }}
                >
                  Finalizar Compra →
                </Button>
              </Flex>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default Cart;
